import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LogoutResolver } from 'src/app/appServices/logout/logout.resolver';
import { ToasterService } from 'src/app/appUtilities/toaster.service';
import { TokenService } from '../auth/token.service';
import { BYPASS_SERVER_ERROR_INTERCEPTOR } from '../httpConfig/http-context-tokens';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  constructor(
    public toastMessage: ToasterService,
    private injector: Injector
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const bypassGlobalErrorHandler = request.context.get(BYPASS_SERVER_ERROR_INTERCEPTOR);

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error, request, next, true, !bypassGlobalErrorHandler);
      })
    );
  }

  private handleError(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler,
    allowRefresh: boolean,
    handleGlobally: boolean
  ): Observable<HttpEvent<any>> {
    if (!handleGlobally) {
      return throwError(error);
    }

    if (error?.error === 'Unauthorized' || error?.status === 401) {
      if (this.hasNewerStoredToken(request)) {
        return this.retryWithCurrentToken(request, next);
      }

      if (allowRefresh && this.canRefreshSession(request)) {
        return this.refreshSessionAndRetry(request, next);
      }

      if (this.isLoginRequest(request.url)) {
        return throwError('Invalid Credentials');
      }

      return this.endSession('Session timeout, Please login again');
    }

    if (error?.status === 500 && this.getServerMessage(error) === 'No Logged In User Found') {
      if (allowRefresh && this.canRefreshSession(request)) {
        return this.refreshSessionAndRetry(request, next);
      }
      return this.endSession('Session timeout, Please login again');
    }

    if (error?.status === 500) {
      return throwError(this.getServerMessage(error) || 'Something went wrong.');
    }

    return throwError(this.getServerMessage(error) || 'Something went wrong.');
  }

  private refreshSessionAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const tokenService = this.injector.get(TokenService);

    return tokenService.refreshSession().pipe(
      catchError((error) => {
        if (tokenService.shouldEndSessionForRefreshError(error)) {
          return this.endSession('Session timeout, Please login again');
        }

        return throwError(this.getServerMessage(error) || 'Unable to refresh session. Please check the connection and try again.');
      }),
      switchMap(() => {
        const retryRequest = request.clone({
          headers: tokenService.createTokenHeader(request.headers)
        });

        return next.handle(retryRequest).pipe(
          catchError((retryError: HttpErrorResponse) => {
            return this.handleError(retryError, retryRequest, next, false, true);
          })
        );
      })
    );
  }

  private retryWithCurrentToken(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const tokenService = this.injector.get(TokenService);
    const retryRequest = request.clone({
      headers: tokenService.createTokenHeader(request.headers)
    });

    return next.handle(retryRequest).pipe(
      catchError((retryError: HttpErrorResponse) => {
        return this.handleError(retryError, retryRequest, next, false, true);
      })
    );
  }

  private canRefreshSession(request: HttpRequest<any>): boolean {
    return !this.isLoginRequest(request.url)
      && !this.isRefreshRequest(request.url)
      && this.injector.get(TokenService).hasRefreshToken();
  }

  private isLoginRequest(url: string): boolean {
    return url.includes('/authenticate');
  }

  private isRefreshRequest(url: string): boolean {
    return url.includes('/auth/refresh') || url.includes('/dashboard/auth/refresh');
  }

  private hasNewerStoredToken(request: HttpRequest<any>): boolean {
    const currentToken = localStorage.getItem('token');
    const requestToken = this.getBearerToken(request);
    return !!currentToken && !!requestToken && currentToken !== requestToken;
  }

  private getBearerToken(request: HttpRequest<any>): string {
    const authorizationHeader = request.headers.get('Authorization');
    if (!authorizationHeader?.startsWith('Bearer ')) {
      return null;
    }
    return authorizationHeader.substring(7);
  }

  private getServerMessage(error: HttpErrorResponse): string {
    if (!error) {
      return null;
    }

    if (error.status === 0) {
      return 'Unable to reach Tastio. Please check the connection and try again.';
    }

    const body = error.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (body?.notification) {
      return body.notification;
    }

    if (body?.message) {
      return body.message;
    }

    const details = body?.details;
    if (Array.isArray(details) && details.length) {
      return details[0];
    }

    const validationErrors = body?.validationErrors;
    if (validationErrors && typeof validationErrors === 'object') {
      const firstError = Object.values(validationErrors).find(Boolean);
      if (firstError) {
        return String(firstError);
      }
    }

    if (body?.code) {
      return body.code;
    }

    return error.message || null;
  }

  private endSession(errorMessage: string): Observable<HttpEvent<any>> {
    this.injector.get(TokenService).clearToken();
    localStorage.removeItem('username');
    this.logout();
    this.toastMessage.errorToastWithClose(errorMessage);
    return throwError(errorMessage);
  }

  private logout() {
    this.injector.get(LogoutResolver).logout();
  }
}
